import React, { useState, useRef, useEffect } from 'react'
import { isEmpty } from 'lodash'
import classNames from 'classnames'

import { SKIP_MODIFIER } from '@/utils/constants'
import { Button } from '@/components/buttons/Button'

import { SkipButton } from './components/SkipButton'
import { PlayPauseButton } from './components/PlayPauseButton'
import { VolumeSlider } from './components/VolumeSlider'
import { PlaybackRateDropdown } from './components/PlaybackRateDropdown'
import { AudioTrackProgress } from './components/AudioTrackProgress'
import { AudioTrackTime } from './components/AudioTrackTime'

import './MediaPlayer.scss'

export const MediaPlayerPlaceholder = ({
  callAudio,
  callVideo,
  handleSelectMediaPlayer,
  progress = 0,
  useMinimalUi = false,
}) => {
  const audioRef = useRef()
  const videoRef = useRef()
  const audioIsAvailable = callAudio?.audio_status === 'available' && callAudio.audio_url
  const videoIsAvailable = callVideo?.video_status === 'available' && !isEmpty(callVideo.video_urls)
  const [duration, setDuration] = useState(0)
  const calculatedProgress = progress >= duration ? 0 : progress

  const handleScrub = (event) => {
    const newTime = event.target.valueAsNumber

    handleSelectMediaPlayer(newTime)
  }

  useEffect(() => {
    const durationListener = (event) => {
      setDuration(event.target.duration)
    }

    if (audioIsAvailable && audioRef.current) {
      audioRef.current.addEventListener('loadedmetadata', durationListener)
    } else if (videoIsAvailable && videoRef.current) {
      videoRef.current.addEventListener('loadedmetadata', durationListener)
    }

    return () => {
      if (audioIsAvailable && audioRef.current) {
        audioRef.current.removeEventListener('loadedmetadata', durationListener)
      } else if (videoIsAvailable && videoRef.current) {
        videoRef.current.removeEventListener('loadedmetadata', durationListener)
      }
    }
  }, [audioIsAvailable, videoIsAvailable])

  return (
    <div className="media-player-container placeholder">
      {audioIsAvailable && <audio src={callAudio.audio_url} ref={audioRef} />}
      {videoIsAvailable && <video src={callVideo.video_urls[0]} ref={videoRef} hidden />}
      <div
        className={classNames('audio-player', { 'audio-player--minimal': useMinimalUi })}
        data-testid="audio-player-placeholder"
      >
        <div className="audio-player-controls" data-testid="audio-controls">
          <PlayPauseButton
            isDisabled={duration === 0}
            togglePlayState={() => handleSelectMediaPlayer(progress)}
          />
          {!useMinimalUi && (
            <>
              <div className="flex-align-center small-gap">
                <SkipButton modifier={SKIP_MODIFIER} isDisabled />
                <SkipButton modifier={SKIP_MODIFIER} isForwardSkip isDisabled />
              </div>
              <div className="audio-player-track" data-testid="audio-progress-track">
                <AudioTrackTime duration={duration} progress={progress} />
                <AudioTrackProgress
                  isDisabled={duration === 0}
                  duration={duration}
                  progress={calculatedProgress}
                  handleScrub={handleScrub}
                />
              </div>
              <VolumeSlider volume={0} isDisabled />
            </>
          )}
        </div>
        {!useMinimalUi && (
          <div className="audio-player-options">
            <PlaybackRateDropdown isDisabled playbackRate={1} />
            <Button small disabled>
              Show Transcript
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
